import { useState } from "react";
import { Sparkles, ThumbsUp, ThumbsDown } from "lucide-react";
import { toArabicNumerals } from "@/lib/arabicNumerals";
import { useClampOverflow } from "@/hooks/useClampOverflow";

type ReviewSummary = {
  text: string;
  pros: string[];
  cons: string[];
  sentiment: "positive" | "mixed" | "negative";
  reviewCount: number;
  sources: {
    yetfarrag: number;
    facebook_group: number;
    google_maps: number;
  };
  generatedAt: string;
};

type StoreReviewSummaryProps = {
  summary: ReviewSummary;
};

const sentimentConfig: Record<
  ReviewSummary["sentiment"],
  { label: string; color: string; bg: string }
> = {
  positive: { label: "انطباع إيجابي", color: "#047857", bg: "#ecfdf5" },
  mixed: { label: "انطباع متباين", color: "#b45309", bg: "#fffbeb" },
  negative: { label: "انطباع سلبي", color: "#b91c1c", bg: "#fef2f2" },
};

export default function StoreReviewSummary({ summary }: StoreReviewSummaryProps) {
  // Mobile clamp toggle — deterministic initial value (SSR-safe). Desktop never clamps.
  const [expanded, setExpanded] = useState(false);
  // Measured overflow: the button only appears when the clamp actually hides content.
  const { ref, isTruncated } = useClampOverflow<HTMLParagraphElement>(
    expanded,
    summary?.text
  );

  if (!summary?.text) return null;

  const sentiment = sentimentConfig[summary.sentiment] ?? sentimentConfig.mixed;

  return (
    <div>
      {/* Heading */}
      <h2
        style={{
          fontFamily: "var(--ys-font)",
          fontWeight: 700,
          fontSize: 20,
          color: "var(--ys-ink)",
          margin: "0 0 16px 0",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Sparkles size={18} style={{ color: "var(--ys-primary)" }} />
        ملخص التقييمات
      </h2>

      <div
        style={{
          background: "var(--ys-paper)",
          borderRadius: "var(--ys-r-md)",
          border: "1px solid var(--ys-rule)",
          boxShadow: "var(--ys-shadow-1)",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* Sentiment badge */}
        <span
          style={{
            alignSelf: "flex-start",
            fontFamily: "var(--ys-font)",
            fontSize: 12,
            fontWeight: 600,
            color: sentiment.color,
            background: sentiment.bg,
            borderRadius: 999,
            padding: "3px 10px",
          }}
        >
          {sentiment.label}
        </span>

        {/* Summary text — CSS clamp only on mobile so the full text stays in SSR HTML */}
        <p
          ref={ref}
          className={expanded ? undefined : "line-clamp-3 lg:line-clamp-none"}
          style={{
            fontFamily: "var(--ys-font)",
            fontSize: 15,
            color: "var(--ys-ink-2)",
            lineHeight: 1.8,
            margin: 0,
          }}
        >
          {summary.text}
        </p>
        {isTruncated && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="lg:hidden self-start"
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ys-primary)",
              background: "transparent",
              border: 0,
              padding: 0,
              marginTop: -8,
              cursor: "pointer",
            }}
          >
            {expanded ? "عرض أقل" : "عرض المزيد"}
          </button>
        )}

        {/* Pros / Cons chips */}
        {(summary.pros.length > 0 || summary.cons.length > 0) && (
          <div className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-hide lg:flex-wrap">
            {summary.pros.map((pro) => (
              <span
                key={pro}
                className="shrink-0"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  fontFamily: "var(--ys-font)",
                  fontSize: 12.5,
                  color: "#047857",
                  background: "#ecfdf5",
                  border: "1px solid #a7f3d0",
                  borderRadius: 999,
                  padding: "4px 12px",
                }}
              >
                <ThumbsUp size={12} />
                {pro}
              </span>
            ))}
            {summary.cons.map((con) => (
              <span
                key={con}
                className="shrink-0"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  fontFamily: "var(--ys-font)",
                  fontSize: 12.5,
                  color: "#b91c1c",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 999,
                  padding: "4px 12px",
                }}
              >
                <ThumbsDown size={12} />
                {con}
              </span>
            ))}
          </div>
        )}

        {/* Footnote */}
        <p
          style={{
            fontFamily: "var(--ys-font)",
            fontSize: 11.5,
            color: "var(--ys-ink-3)",
            margin: 0,
          }}
        >
          ملخص آلي بناءً على {toArabicNumerals(summary.reviewCount)} تقييم من يتفرج وجروبات فيسبوك وخرائط جوجل
        </p>
      </div>
    </div>
  );
}
